import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/OrganizationContext';
import { campaignTasksRepository } from '../repositories/campaignTasks.repository';
import { campaignKeys } from '../services/campaign.keys';
import type {
  CampaignTask,
  CreateTaskData,
  TaskPriority,
  TaskStatus,
  UpdateTaskData,
} from '../types/campaign.types';

export type { TaskStatus, TaskPriority, CampaignTask, CreateTaskData, UpdateTaskData };

export const useCampaignTasks = (
  campaignId: string,
  filters?: { status?: TaskStatus; assignedTo?: string }
) => {
  return useQuery({
    queryKey: campaignKeys.tasks(campaignId, filters),
    queryFn: async () => {
      return campaignTasksRepository.getTasks(campaignId, filters);
    },
    enabled: !!campaignId,
  });
};

export const useCreateCampaignTask = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({
      campaignId,
      taskData,
    }: {
      campaignId: string;
      taskData: CreateTaskData;
    }) => {
      return campaignTasksRepository.createTask(campaignId, taskData, currentOrganization?.id);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.tasks(variables.campaignId) });
      toast({
        title: 'Task created',
        description: 'Your task has been added successfully.',
      });
    },
    onError: (error) => {
      console.error('Task creation error:', error);
      toast({
        title: 'Error',
        description: 'Failed to create task. Please try again.',
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateCampaignTask = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      taskId,
      taskData,
    }: {
      taskId: string;
      campaignId: string;
      taskData: UpdateTaskData;
    }) => {
      return campaignTasksRepository.updateTask(taskId, taskData);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.tasks(variables.campaignId) });
      toast({
        title: 'Task updated',
        description: 'Your task has been updated successfully.',
      });
    },
    onError: (error) => {
      console.error('Task update error:', error);
      toast({
        title: 'Error',
        description: 'Failed to update task. Please try again.',
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteCampaignTask = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ taskId }: { taskId: string; campaignId: string }) => {
      return campaignTasksRepository.deleteTask(taskId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.tasks(variables.campaignId) });
      toast({
        title: 'Task deleted',
        description: 'Your task has been deleted successfully.',
      });
    },
    onError: (error) => {
      console.error('Task deletion error:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete task. Please try again.',
        variant: 'destructive',
      });
    },
  });
};
